import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  Visibility as OverviewIcon,
  AccountTree as AutoFlowIcon,
  BarChart as KpisIcon,
  History as AuditIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ChevronRight as CollapseIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import { Can } from "src/helpers/Auth";
import { CAPAIProductID } from "src/reusable/products";
import { generateLink } from "src/routes";
import { Loading } from "src/reusable/LoadingSpinner";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useAllAutomationConfigTemplates } from "./services/AutomationListServices";
import { TEMPLATE_TAB_DEFS } from "./shared/TemplateNav";
import ScreeningKindBadge from "./llm/ScreeningKindBadge";
import { useLlmScreeningEnabled } from "src/helpers/licensedRolloutGates";
import {
  CreateAutomationConfigTemplateModal,
  UpdateAutomationConfigTemplateModal,
  DeleteAutomationConfigTemplateModal,
} from "src/views/forms/AutomationConfigTemplateModals";

const TAB_COLORS = Object.fromEntries(
  TEMPLATE_TAB_DEFS.map((t) => [t.value, t.color]),
);

const tId = "cap-auto";

const AutomationConfigTemplatePropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  screening_kind: PropTypes.string,
  child_automation_config_templates: PropTypes.array.isRequired,
});

const ActionButton = ({ title, icon, onClick, testId, color, iconOnly }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    onClick();
  };

  // Narrow widths drop the label to keep all actions on one line; the
  // Tooltip keeps the meaning discoverable.
  if (iconOnly) {
    return (
      <Tooltip title={title} placement="top" arrow>
        <IconButton
          size="small"
          onClick={handleClick}
          data-testid={testId}
          aria-label={title}
          sx={{
            color: color || "text.secondary",
            border: "1px solid",
            borderColor: color || "divider",
            borderRadius: 1,
            "&:hover": {
              borderColor: color || "text.primary",
              bgcolor: "action.hover",
            },
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={icon}
      onClick={handleClick}
      data-testid={testId}
      sx={{
        color: color || "text.secondary",
        borderColor: color || "divider",
        textTransform: "none",
        whiteSpace: "nowrap",
        "&:hover": {
          borderColor: color || "text.primary",
          bgcolor: "action.hover",
        },
      }}
    >
      {title}
    </Button>
  );
};

ActionButton.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  testId: PropTypes.string,
  color: PropTypes.string,
  iconOnly: PropTypes.bool,
};

const MobileActionMenu = ({ template, showAdmin, hasChildren }) => {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        data-testid={`${tId}-btn-menu-${template.id}`}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        data-testid={`${tId}-menu-${template.id}`}
      >
        <Can
          perform="autotemplate:read"
          yes={() => (
            <MenuItem
              data-testid={`${tId}-btn-overview-${template.id}`}
              onClick={() => {
                setAnchorEl(null);
                history.push(
                  generateLink("Automation Template", {
                    automation_config_template_id: template.id,
                  }),
                );
              }}
            >
              <ListItemIcon>
                <OverviewIcon
                  fontSize="small"
                  sx={{ color: TAB_COLORS.overview }}
                />
              </ListItemIcon>
              <ListItemText>Overview</ListItemText>
            </MenuItem>
          )}
        />
        <Can
          perform="autoconfig:read"
          yes={() => (
            <MenuItem
              data-testid={`${tId}-btn-autoflow-${template.id}`}
              onClick={() => {
                setAnchorEl(null);
                history.push(
                  generateLink("AutoFlow", {
                    automation_config_template_id: template.id,
                  }),
                );
              }}
            >
              <ListItemIcon>
                <AutoFlowIcon
                  fontSize="small"
                  sx={{ color: TAB_COLORS.autoflow }}
                />
              </ListItemIcon>
              <ListItemText>AutoFlow Config</ListItemText>
            </MenuItem>
          )}
        />
        {!hasChildren && (
          <Can
            perform="autodata:read"
            yes={() => [
              <MenuItem
                key="kpis"
                data-testid={`${tId}-btn-kpis-${template.id}`}
                onClick={() => {
                  setAnchorEl(null);
                  history.push(
                    generateLink("Automation KPIs", {
                      automation_config_template_id: template.id,
                    }),
                  );
                }}
              >
                <ListItemIcon>
                  <KpisIcon fontSize="small" sx={{ color: TAB_COLORS.kpis }} />
                </ListItemIcon>
                <ListItemText>Performance Metrics</ListItemText>
              </MenuItem>,
              <MenuItem
                key="audit"
                data-testid={`${tId}-btn-audit-${template.id}`}
                onClick={() => {
                  setAnchorEl(null);
                  history.push(
                    generateLink("Automation Records", {
                      automation_config_template_id: template.id,
                    }),
                  );
                }}
              >
                <ListItemIcon>
                  <AuditIcon
                    fontSize="small"
                    sx={{ color: TAB_COLORS.audit }}
                  />
                </ListItemIcon>
                <ListItemText>Audit History</ListItemText>
              </MenuItem>,
            ]}
          />
        )}
        {showAdmin && [
          <Can
            key="edit"
            perform="autotemplate:update"
            yes={() => (
              <>
                <Divider />
                <MenuItem
                  data-testid={`btn-update-automationtemplate-${template.id}`}
                  onClick={() => {
                    setAnchorEl(null);
                    setShowUpdateModal(true);
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              </>
            )}
          />,
          <Can
            key="delete"
            perform="autotemplate:delete"
            yes={() => (
              <MenuItem
                data-testid={`btn-delete-automationtemplate-${template.id}`}
                onClick={() => {
                  setAnchorEl(null);
                  setShowDeleteModal(true);
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
                </ListItemIcon>
                <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
              </MenuItem>
            )}
          />,
        ]}
      </Menu>

      {showUpdateModal && (
        <UpdateAutomationConfigTemplateModal
          id={template.id}
          isVisible
          onClose={() => setShowUpdateModal(false)}
        />
      )}
      {showDeleteModal && (
        <DeleteAutomationConfigTemplateModal
          id={template.id}
          isVisible
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};

MobileActionMenu.propTypes = {
  template: AutomationConfigTemplatePropType.isRequired,
  showAdmin: PropTypes.bool,
  hasChildren: PropTypes.bool,
};

const TemplateRow = ({
  template,
  showAdmin,
  isChild,
  isMobile,
  iconOnly,
  llmScreeningEnabled,
}) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const hasChildren = template.child_automation_config_templates.length > 0;

  return (
    <>
      <Box
        data-testid={`cap-auto-cell-name-${template.id}`}
        onClick={() => history.push(`/products/cap-ai/auto/act/${template.id}`)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          pl: isChild ? { xs: 3, sm: 6 } : { xs: 1.5, sm: 2.5 },
          cursor: "pointer",
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {/* Expand toggle for parents */}
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            data-testid={`cap-auto-children-${template.id}`}
          >
            {expanded ? (
              <ExpandIcon fontSize="small" />
            ) : (
              <CollapseIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 30 }} />
        )}

        {/* Name + actions column — the name spans the full row width on
            its own line; the ID line and the action buttons share the
            line below, so a long name is no longer squeezed by the
            buttons. The name is single-line (noWrap), so a Tooltip with
            the full name is always attached as a hover fallback for the
            cases where it still overflows. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={template.name} placement="top-start">
            <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
              {template.name}
            </Typography>
          </Tooltip>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 1,
              mt: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
            >
              ID: {template.id}
              {hasChildren && (
                <>
                  {" "}
                  &middot; {
                    template.child_automation_config_templates.length
                  }{" "}
                  sub-template
                  {template.child_automation_config_templates.length > 1
                    ? "s"
                    : ""}
                </>
              )}
              <ScreeningKindBadge
                screeningKind={template.screening_kind}
                llmScreeningEnabled={llmScreeningEnabled}
                testId={`cap-auto-screening-kind-${template.id}`}
                sx={{ ml: 1, height: 18, fontSize: "0.7rem" }}
              />
            </Typography>

            {/* Actions — desktop: inline buttons, mobile: overflow menu */}
            {isMobile ? (
              <MobileActionMenu
                template={template}
                showAdmin={showAdmin}
                hasChildren={hasChildren}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
                data-testid={`cap-auto-row-actions-${template.id}`}
              >
                <Can
                  perform="autotemplate:read"
                  yes={() => (
                    <ActionButton
                      title="Overview"
                      icon={<OverviewIcon fontSize="small" />}
                      iconOnly={iconOnly}
                      testId={`${tId}-btn-overview-${template.id}`}
                      color={TAB_COLORS.overview}
                      onClick={() =>
                        history.push(
                          generateLink("Automation Template", {
                            automation_config_template_id: template.id,
                          }),
                        )
                      }
                    />
                  )}
                />
                <Can
                  perform="autoconfig:read"
                  yes={() => (
                    <ActionButton
                      title="AutoFlow Config"
                      icon={<AutoFlowIcon fontSize="small" />}
                      iconOnly={iconOnly}
                      testId={`${tId}-btn-autoflow-${template.id}`}
                      color={TAB_COLORS.autoflow}
                      onClick={() =>
                        history.push(
                          generateLink("AutoFlow", {
                            automation_config_template_id: template.id,
                          }),
                        )
                      }
                    />
                  )}
                />
                {!hasChildren && (
                  <Can
                    perform="autodata:read"
                    yes={() => (
                      <>
                        <ActionButton
                          title="Performance Metrics"
                          icon={<KpisIcon fontSize="small" />}
                          iconOnly={iconOnly}
                          testId={`${tId}-btn-kpis-${template.id}`}
                          color={TAB_COLORS.kpis}
                          onClick={() =>
                            history.push(
                              generateLink("Automation KPIs", {
                                automation_config_template_id: template.id,
                              }),
                            )
                          }
                        />
                        <ActionButton
                          title="Audit History"
                          icon={<AuditIcon fontSize="small" />}
                          iconOnly={iconOnly}
                          testId={`${tId}-btn-audit-${template.id}`}
                          color={TAB_COLORS.audit}
                          onClick={() =>
                            history.push(
                              generateLink("Automation Records", {
                                automation_config_template_id: template.id,
                              }),
                            )
                          }
                        />
                      </>
                    )}
                  />
                )}
                {showAdmin && (
                  <>
                    <Can
                      perform="autotemplate:update"
                      yes={() => (
                        <ActionButton
                          title="Edit"
                          icon={<EditIcon fontSize="small" />}
                          iconOnly={iconOnly}
                          testId={`btn-update-automationtemplate-${template.id}`}
                          color="primary.main"
                          onClick={() => setShowUpdateModal(true)}
                        />
                      )}
                    />
                    <Can
                      perform="autotemplate:delete"
                      yes={() => (
                        <ActionButton
                          title="Delete"
                          icon={<DeleteIcon fontSize="small" />}
                          iconOnly={iconOnly}
                          testId={`btn-delete-automationtemplate-${template.id}`}
                          color="error.main"
                          onClick={() => setShowDeleteModal(true)}
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Children */}
      {hasChildren && (
        <Collapse in={expanded}>
          {template.child_automation_config_templates.map((child) => (
            <TemplateRow
              key={child.id}
              template={child}
              showAdmin={showAdmin}
              isChild
              isMobile={isMobile}
              iconOnly={iconOnly}
              llmScreeningEnabled={llmScreeningEnabled}
            />
          ))}
        </Collapse>
      )}

      {/* Modals (desktop only — mobile modals live in MobileActionMenu) */}
      {!isMobile && showUpdateModal && (
        <UpdateAutomationConfigTemplateModal
          id={template.id}
          isVisible
          onClose={() => setShowUpdateModal(false)}
        />
      )}
      {!isMobile && showDeleteModal && (
        <DeleteAutomationConfigTemplateModal
          id={template.id}
          isVisible
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};

TemplateRow.propTypes = {
  template: AutomationConfigTemplatePropType.isRequired,
  showAdmin: PropTypes.bool,
  isChild: PropTypes.bool,
  isMobile: PropTypes.bool,
  iconOnly: PropTypes.bool,
  llmScreeningEnabled: PropTypes.bool,
};

TemplateRow.defaultProps = {
  showAdmin: false,
  isChild: false,
  isMobile: false,
  iconOnly: false,
  llmScreeningEnabled: false,
};

export const AutomationConfigTemplateTable = ({
  parentAutomationConfigTemplateId,
  showAdminButtons,
  productFk,
}) => {
  // Progressive collapse of the row actions:
  //  - <900px (tablet/phone): overflow kebab menu
  //  - 900–1200px (narrow desktop): icon-only buttons + tooltips, so all
  //    six fit on one line instead of wrapping
  //  - ≥1200px: full icon + text buttons
  const { isTablet, isCompact } = useResponsive();
  const iconOnly = isCompact && !isTablet;
  // read once here and handed to every row (and its children) for the
  // screening-kind badge, rather than one flag subscription per row
  const llmScreeningEnabled = useLlmScreeningEnabled();
  const {
    data: allAutomationConfigTemplates,
    isLoading: loadingAllAutomationConfigTemplates,
  } = useAllAutomationConfigTemplates(
    parentAutomationConfigTemplateId,
    null,
    productFk,
  );

  if (loadingAllAutomationConfigTemplates) {
    return <Loading height="20em" width="20em" />;
  }

  if (
    !allAutomationConfigTemplates ||
    allAutomationConfigTemplates.length === 0
  ) {
    return (
      <Box
        sx={{ textAlign: "center", py: 6 }}
        data-testid="cap-auto-table-empty"
      >
        <Typography color="text.secondary">
          No automation templates yet. Create one to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Card variant="outlined" data-testid="cap-auto-table">
      {/* Column header — hide in compact (overflow-menu) layout */}
      {!isTablet && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2.5,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Box sx={{ width: 30 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, fontWeight: 600, ml: 1 }}
          >
            Template
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Actions
          </Typography>
        </Box>
      )}

      {allAutomationConfigTemplates.map((template) => (
        <TemplateRow
          key={template.id}
          template={template}
          showAdmin={showAdminButtons}
          isMobile={isTablet}
          iconOnly={iconOnly}
          llmScreeningEnabled={llmScreeningEnabled}
        />
      ))}
    </Card>
  );
};

AutomationConfigTemplateTable.propTypes = {
  parentAutomationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  showAdminButtons: PropTypes.bool,
  productFk: PropTypes.number,
};

AutomationConfigTemplateTable.defaultProps = {
  showAdminButtons: false,
};

const AutomationConfigTemplateList = ({ parentAutomationConfigTemplateId }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} data-testid="cap-auto-container">
      {/* Header — stacks on mobile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ mb: 1, fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
            data-testid="cap-auto-heading"
          >
            Automation Templates
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 700 }}
          >
            Each template represents a process you are automating. Configure
            screening workflows, track performance metrics, and audit previous
            runs.
          </Typography>
        </Box>
        <Can
          perform="autotemplate:create"
          yes={() => (
            <span
              data-testid="cap-auto-btn-create"
              data-attr="cap-ai:btn:create-automation-template"
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                fullWidth={false}
                sx={{
                  whiteSpace: "nowrap",
                  alignSelf: { xs: "stretch", sm: "auto" },
                }}
              >
                New Template
              </Button>
            </span>
          )}
        />
      </Box>

      {/* Table */}
      <AutomationConfigTemplateTable
        parentAutomationConfigTemplateId={parentAutomationConfigTemplateId}
        showAdminButtons={true}
        productFk={CAPAIProductID}
      />

      {/* Create modal */}
      <CreateAutomationConfigTemplateModal
        isVisible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        defaultProductFk={CAPAIProductID}
      />
    </Box>
  );
};

AutomationConfigTemplateList.propTypes = {
  parentAutomationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

AutomationConfigTemplateList.defaultProps = {
  parentAutomationConfigTemplateId: null,
};

export default AutomationConfigTemplateList;
